/**
 * Client-side storage helpers with TTL + lightweight LRU pruning.
 * Keeps localStorage usage bounded and avoids quota failures.
 */

type IndexEntry = {
  key: string;
  lastAccessed: number;
  bytes: number;
  meta?: Record<string, unknown>;
};

type StoredEnvelope<T> = {
  v: T;
  ts: number;
  exp?: number;
  meta?: Record<string, unknown>;
};

const INDEX_PREFIX = "sv_storage_index:";

function now() {
  return Date.now();
}

function estimateBytes(value: unknown) {
  // Rough estimate: 2 bytes per char (UTF-16), plus overhead.
  try {
    return JSON.stringify(value).length * 2;
  } catch {
    return 0;
  }
}

function readIndex(namespace: string): Record<string, IndexEntry> {
  if (typeof window === "undefined") return {};
  const raw = localStorage.getItem(`${INDEX_PREFIX}${namespace}`);
  if (!raw) return {};
  try {
    return JSON.parse(raw) as Record<string, IndexEntry>;
  } catch {
    return {};
  }
}

function writeIndex(namespace: string, index: Record<string, IndexEntry>) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(`${INDEX_PREFIX}${namespace}`, JSON.stringify(index));
  } catch {
    // ignore
  }
}

export function pruneNamespace(opts: {
  namespace: string;
  maxEntries?: number;
  maxAgeMs?: number;
  keepIfMeta?: (meta: Record<string, unknown> | undefined) => boolean;
}) {
  if (typeof window === "undefined") return;
  const { namespace, maxEntries, maxAgeMs, keepIfMeta } = opts;
  const index = readIndex(namespace);
  const entries = Object.values(index);
  const cutoff = maxAgeMs ? now() - maxAgeMs : undefined;

  // 1) Age-based pruning first
  if (cutoff) {
    for (const e of entries) {
      const keep = keepIfMeta?.(e.meta);
      if (keep) continue;
      if (e.lastAccessed < cutoff) {
        try {
          localStorage.removeItem(e.key);
        } catch {}
        delete index[e.key];
      }
    }
  }

  // 2) LRU-based pruning
  if (maxEntries && maxEntries > 0) {
    const remaining = Object.values(index).sort(
      (a, b) => a.lastAccessed - b.lastAccessed,
    );
    let removable = remaining.filter((e) => !keepIfMeta?.(e.meta));
    while (Object.keys(index).length > maxEntries && removable.length > 0) {
      const victim = removable.shift();
      if (!victim) break;
      try {
        localStorage.removeItem(victim.key);
      } catch {}
      delete index[victim.key];
      removable = removable.filter((e) => e.key !== victim.key);
    }
  }

  writeIndex(namespace, index);
}

export function getJSON<T>(
  key: string,
  opts?: { namespace?: string; touch?: boolean },
): T | null {
  if (typeof window === "undefined") return null;
  const raw = localStorage.getItem(key);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as StoredEnvelope<T> | T;

    // Support both envelope and plain JSON (backward compatible).
    const envelope =
      parsed && typeof parsed === "object" && "v" in (parsed as any)
        ? (parsed as StoredEnvelope<T>)
        : null;

    if (envelope) {
      if (envelope.exp && envelope.exp < now()) {
        try {
          localStorage.removeItem(key);
        } catch {}
        return null;
      }

      if (opts?.namespace && opts.touch !== false) {
        const index = readIndex(opts.namespace);
        const bytes = estimateBytes(envelope);
        index[key] = {
          key,
          lastAccessed: now(),
          bytes,
          meta: envelope.meta,
        };
        writeIndex(opts.namespace, index);
      }

      return envelope.v ?? null;
    }

    if (opts?.namespace && opts.touch !== false) {
      const index = readIndex(opts.namespace);
      const bytes = estimateBytes(parsed);
      index[key] = { key, lastAccessed: now(), bytes };
      writeIndex(opts.namespace, index);
    }

    return parsed as T;
  } catch {
    return null;
  }
}

export function setJSON<T>(
  key: string,
  value: T,
  opts?: {
    namespace?: string;
    ttlMs?: number;
    meta?: Record<string, unknown>;
    maxEntries?: number;
    maxAgeMs?: number;
    keepIfMeta?: (meta: Record<string, unknown> | undefined) => boolean;
  },
): boolean {
  if (typeof window === "undefined") return false;

  const env: StoredEnvelope<T> = {
    v: value,
    ts: now(),
    exp: opts?.ttlMs ? now() + opts.ttlMs : undefined,
    meta: opts?.meta,
  };

  // Best-effort prune before write.
  if (opts?.namespace && (opts.maxEntries || opts.maxAgeMs)) {
    pruneNamespace({
      namespace: opts.namespace,
      maxEntries: opts.maxEntries,
      maxAgeMs: opts.maxAgeMs,
      keepIfMeta: opts.keepIfMeta,
    });
  }

  try {
    localStorage.setItem(key, JSON.stringify(env));
  } catch {
    // Try one more prune pass, then retry once.
    if (opts?.namespace) {
      pruneNamespace({
        namespace: opts.namespace,
        maxEntries: opts.maxEntries ?? 50,
        maxAgeMs: opts.maxAgeMs,
        keepIfMeta: opts.keepIfMeta,
      });
      try {
        localStorage.setItem(key, JSON.stringify(env));
      } catch {
        return false;
      }
    } else {
      return false;
    }
  }

  if (opts?.namespace) {
    const index = readIndex(opts.namespace);
    index[key] = {
      key,
      lastAccessed: now(),
      bytes: estimateBytes(env),
      meta: opts.meta,
    };
    writeIndex(opts.namespace, index);
  }

  return true;
}

export function removeKey(key: string, namespace?: string) {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(key);
  } catch {}
  if (namespace) {
    const index = readIndex(namespace);
    delete index[key];
    writeIndex(namespace, index);
  }
}
